import { Calendar, MapPin, Star, User, Flag, Crown } from "lucide-react";
import { useState } from "react";
import { Button } from "../ui/button";
import { Link } from "react-router-dom";
import { Badge } from "../ui/badge";
import { ReportModal } from "../ReportModal";


export const ProfileCard = ({
  id,
  name,
  avatar,
  location,
  memberSince,
  bio,
  rating,
  listingCount,
  exchangesCount,
  isCurrentUser = false,
  isFoundingMember = false, // New prop for founding member status
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  return (
    <div className="rounded-xl overflow-hidden bg-white border border-border/60 shadow-sm p-6">
      <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
        {/* Avatar */}
        <div className="relative w-24 h-24 rounded-full overflow-hidden bg-igbanwo-mint/60 flex-shrink-0">
          {avatar ? (
            <>
              <div
                className={`absolute inset-0 bg-gray-200 ${
                  imageLoaded ? "opacity-0" : "opacity-100"
                } transition-opacity duration-300`}
              ></div>
              <img
                src={avatar}
                alt={name}
                className={`w-full h-full object-cover ${
                  imageLoaded ? "opacity-100" : "opacity-0"
                } transition-opacity duration-300`}
                onLoad={() => setImageLoaded(true)}
              />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <User className="w-10 h-10 text-igbanwo-mint" />
            </div>
          )}
        </div>

        {/* User Info */}
        <div className="flex-1 text-center md:text-left">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
            <div className="flex items-center justify-center md:justify-start gap-3 flex-wrap">
              <h2 className="text-2xl font-semibold" title={name}>
                {name}
              </h2>
              {isFoundingMember && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-sm px-2 py-1 gap-1.5">
                  <Crown className="h-3.5 w-3.5" />
                  Founder
                </Badge>
              )}
            </div>
            
            {/* Report Button - only show for other users */}
            {!isCurrentUser && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowReportModal(true)}
                className="text-gray-400 hover:text-red-500 hover:bg-red-50 mt-2 md:mt-0"
                title="Report user"
              >
                <Flag className="w-4 h-4" />
              </Button>
            )}
          </div>

          <div className="flex flex-wrap justify-center md:justify-start gap-3 mb-3 text-sm text-muted-foreground">
            <div className="flex items-center">
              <MapPin className="h-4 w-4 mr-1 text-igbanwo-mint" />
              <span>{location}</span>
            </div>
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-1 text-igbanwo-mint" />
              <span>Member since {memberSince}</span>
            </div>
            <div className="flex items-center">
              <Star className="h-4 w-4 mr-1 text-harvest-500 fill-harvest-500" />
              <span>{rating.toFixed(1)} Rating</span>
            </div>
          </div>

          <p className="text-muted-foreground mb-4">{bio}</p>

          {/* Stats */}
          <div className="flex justify-center md:justify-start gap-4 mb-4">
            <div className="text-center px-4 py-2 bg-igbanwo-mint/10 rounded-md">
              <div className="font-semibold">{listingCount}</div>
              <div className="text-sm text-muted-foreground">Listings</div>
            </div>
            <div className="text-center px-4 py-2 bg-igbanwo-mint/10 rounded-md">
              <div className="font-semibold">{exchangesCount}</div>
              <div className="text-xs text-muted-foreground">Exchanges</div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-center md:justify-start gap-3">
            {isCurrentUser && (
              <Link to="/edit-profile">
                <Button
                  variant="outline"
                  className="border-igbanwo-mint/20 hover:border-igbanwo-mint/40"
                >
                  Edit Profile
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        targetId={id}
        targetType="user"
        targetName={name}
      />
    </div>
  );
};